import { postCall_v2, getCall_v2 } from "services";
import { VENDOR_SEARCH_CONTEXT } from "constants/eddConfig";
import { searchVendors } from "services/availabilityService";
import {
  mapOffDayFromApi,
  mapOffDayToApi,
  mapVendorSearchToLocations,
  mapWorkingHoursFromApi,
  mapWorkingHoursToApi,
} from "utils/eddUtils";
import { downloadCsv } from "utils";

// nginx proxies /availability-api/ to supply_service/api/
const AVAILABILITY_BASE_URL = "/availability-api";
const EDD_BASE_URL = `${AVAILABILITY_BASE_URL}/v1/edd`;

export const EDD_URLS = {
  // Vendor working hours & off-days
  VENDOR_WORKING_HOURS: (vendorLocationKey) =>
    `${EDD_BASE_URL}/working-hours/vendor_location/${vendorLocationKey}`,
  VENDOR_WORKING_HOURS_UPSERT: (vendorLocationKey) =>
    `${EDD_BASE_URL}/working-hours/vendor_location/${vendorLocationKey}/upsert`,
  VENDOR_WORKING_HOURS_UPLOAD: (vendorLocationKey) =>
    `${EDD_BASE_URL}/working-hours/vendor_location/${vendorLocationKey}/upload`,
  VENDOR_OFF_DAYS: (vendorLocationKey) =>
    `${EDD_BASE_URL}/off-days/vendor_location/${vendorLocationKey}`,
  VENDOR_OFF_DAYS_UPSERT: (vendorLocationKey) =>
    `${EDD_BASE_URL}/off-days/vendor_location/${vendorLocationKey}/upsert`,
  VENDOR_OFF_DAYS_UPLOAD: (vendorLocationKey) =>
    `${EDD_BASE_URL}/off-days/vendor_location/${vendorLocationKey}/upload`,

  // SLA bulk file upload & history listing & template download
  SLA_FILE_UPLOAD: `${AVAILABILITY_BASE_URL}/v1/files/upload`,
  FILE_LISTING: `${AVAILABILITY_BASE_URL}/v1/file-listing`,
  DOWNLOAD_TEMPLATE: (templateName) =>
    `${AVAILABILITY_BASE_URL}/v1/download/template/${templateName}/csv`,

  // Order Committed EDD
  ORDER_EDD: (orderNumber) => `/joms/api/external/v1/order/edd/${orderNumber}`,
};

// supply service wraps errors as { statusCode, status, data, error }
const getErrorMessage = (err, fallback) => {
  const apiError = err?.data?.error;
  return (
    apiError?.message ||
    (typeof apiError === "string" && apiError) ||
    err?.data?.message ||
    err?.statusText ||
    fallback
  );
};

const EMPTY_ROW_RESULT = {
  successCount: 0,
  failedCount: 0,
  successfulRows: [],
  failedRows: [],
};

const mapRowResult = (data) => ({
  successCount: data?.successCount ?? 0,
  failedCount: data?.failedCount ?? 0,
  successfulRows: data?.successfulRows ?? [],
  failedRows: data?.failedRows ?? [],
});

const fetchList = async (url, mapRow, fallbackError) => {
  const result = { data: [], error: null };
  try {
    const response = await getCall_v2(url);
    const body = response?.data;
    if (Array.isArray(body?.data)) {
      result.data = body.data.map(mapRow);
    } else {
      result.error = getErrorMessage({ data: body }, fallbackError);
    }
  } catch (err) {
    result.error = getErrorMessage(err, fallbackError);
  }
  return result;
};

const postRows = async (url, payload, fallbackError) => {
  const result = { data: EMPTY_ROW_RESULT, error: null };
  try {
    const response = await postCall_v2(url, payload);
    const body = response?.data;
    if (body?.data) {
      result.data = mapRowResult(body.data);
    } else {
      result.error = getErrorMessage({ data: body }, fallbackError);
    }
  } catch (err) {
    result.error = getErrorMessage(err, fallbackError);
  }
  return result;
};

const uploadCsv = (url, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return postRows(url, formData, "Failed to upload file");
};

export const searchVendorLocations = async (vendorId) => {
  try {
    const payload = await searchVendors(
      vendorId.trim().toUpperCase(),
      VENDOR_SEARCH_CONTEXT,
    );
    // payload is either the results array or { data: [], meta_info }
    const results = Array.isArray(payload) ? payload : payload?.data ?? [];
    return { data: mapVendorSearchToLocations(results) };
  } catch (err) {
    return { data: [], error: err?.message || "Failed to search vendor" };
  }
};

// ─── Working hours ────────────────────────────────────────────────────────────

export const fetchVendorWorkingHours = (vendorLocationKey) =>
  fetchList(
    EDD_URLS.VENDOR_WORKING_HOURS(vendorLocationKey),
    mapWorkingHoursFromApi,
    "Failed to fetch working hours",
  );

export const saveVendorWorkingHours = (vendorLocationKey, rows) =>
  postRows(
    EDD_URLS.VENDOR_WORKING_HOURS_UPSERT(vendorLocationKey),
    rows.map(mapWorkingHoursToApi),
    "Failed to save working hours",
  );

export const uploadVendorWorkingHoursFile = (vendorLocationKey, file) =>
  uploadCsv(EDD_URLS.VENDOR_WORKING_HOURS_UPLOAD(vendorLocationKey), file);

// ─── Off days ─────────────────────────────────────────────────────────────────

export const fetchVendorOffDays = (vendorLocationKey) =>
  fetchList(
    EDD_URLS.VENDOR_OFF_DAYS(vendorLocationKey),
    mapOffDayFromApi,
    "Failed to fetch off days",
  );

export const saveVendorOffDays = (vendorLocationKey, rows) =>
  postRows(
    EDD_URLS.VENDOR_OFF_DAYS_UPSERT(vendorLocationKey),
    rows.map(mapOffDayToApi),
    "Failed to save off day",
  );

export const uploadVendorOffDaysFile = (vendorLocationKey, file) =>
  uploadCsv(EDD_URLS.VENDOR_OFF_DAYS_UPLOAD(vendorLocationKey), file);

// ─── SLA File Upload & History ────────────────────────────────────────────────

/**
 * @description Upload SLA CSV file to supply service
 * Endpoint: POST /availability-api/v1/files/upload
 * Payload: form-data with `file` and `fileType`
 */
export const uploadSlaFile = async (slaType, file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileType", slaType);

    const response = await postCall_v2(EDD_URLS.SLA_FILE_UPLOAD, formData);
    const payload = response?.data;

    if (
      payload?.status === "Success" ||
      payload?.statusCode === 202 ||
      response?.status === 202
    ) {
      const uploadData = payload?.data;

      return {
        success: true,
        data: uploadData,
        message: `File uploaded successfully. Upload ID: ${uploadData?.upload_id}`,
      };
    }

    return {
      success: false,
      error: payload?.message || "Failed to upload file",
    };
  } catch (err) {
    const errorMsg =
      err?.data?.errors?.[0]?.message ||
      err?.data?.message ||
      err?.data?.errorMessage ||
      err?.message ||
      "Failed to upload file";

    return {
      success: false,
      error: errorMsg,
      code: err?.data?.errors?.[0]?.code,
    };
  }
};

/**
 * @description Fetch file processing status and history for SLA tables
 * Endpoint: GET /availability-api/v1/file-listing?file_type=<FILE_TYPE>&page=<PAGE>&size=<SIZE>
 */
export const fetchSlaHistory = async (slaType, page = 0, size = 10) => {
  try {
    const response = await getCall_v2(EDD_URLS.FILE_LISTING, {
      file_type: slaType,
      page,
      size,
    });

    const payload = response?.data;

    const extractRawList = (data) => {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.data?.items)) return data.data.items;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.data?.content)) return data.data.content;
      if (Array.isArray(data?.content)) return data.content;
      return [];
    };

    const rawList = extractRawList(payload);

    const mappedRows = rawList.map((item, index) => {
      const changedBy =
        item.changed_by ||
        item.created_by ||
        item.uploaded_by ||
        item.user_email ||
        item.user_id ||
        "-";

      const changedAt =
        item.changed_at ||
        item.created_at ||
        item.uploaded_at ||
        item.created_date ||
        item.timestamp ||
        null;

      const fileName = item.file_name || item.name;
      let changeSummary = item.change_summary || item.summary || "-";
      if (!item.change_summary && !item.summary && fileName) {
        changeSummary = item.upload_id
          ? `${fileName} (ID: ${item.upload_id})`
          : fileName;
      }

      const status = String(
        item.status || item.processing_status || item.upload_status || ""
      ).toUpperCase();

      return {
        id: item.upload_id || item.id || index,
        upload_id: item.upload_id || item.id,
        changed_by: changedBy,
        changed_at: changedAt,
        change_summary: changeSummary,
        status: status,
        gcs_path: item.gcs_path || item.file_path,
        error_file_path: item.error_file_path || item.error_file_url,
        ...item,
      };
    });

    return {
      data: mappedRows,
      pageDetail: payload?.data?.page_detail || payload?.page_detail || {
        page: payload?.page ?? page,
        size: payload?.size ?? size,
        totalItems: payload?.total_items ?? rawList.length,
        totalPages: payload?.total_pages ?? 1,
      },
      error: null,
    };
  } catch (err) {
    const errorMsg =
      err?.data?.errors?.[0]?.message ||
      err?.data?.message ||
      err?.data?.errorMessage ||
      err?.message ||
      "Failed to fetch file listing";

    return {
      data: [],
      error: errorMsg,
    };
  }
};

/**
 * @description Download CSV template for SLA tables or Vendor calendars
 * Endpoint: GET /availability-api/v1/download/template/:templateName/csv
 */
export const downloadTemplateFile = async (templateName) => {
  try {
    const url = EDD_URLS.DOWNLOAD_TEMPLATE(templateName);
    const response = await getCall_v2(url, {}, "blob");
    const data = response?.data;
    if (data) {
      downloadCsv(data, templateName);
      return { success: true };
    }
    return { success: false, error: "No data received" };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Failed to download template"),
    };
  }
};

/**
 * @description Fetch Order EDD details by Order Number
 * Endpoint: GET /joms/api/external/v1/order/edd/:orderNumber
 */
export const fetchOrderEddDetails = async (orderNumber) => {
  if (!orderNumber) {
    return { success: false, error: "Order number is required" };
  }

  try {
    const url = EDD_URLS.ORDER_EDD(orderNumber);
    const response = await getCall_v2(url);
    const data = response?.data;

    return {
      success: true,
      data,
    };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Failed to fetch order delivery date details"),
    };
  }
};

